import { ToolContainer, FileDropZone, DownloadButton, ResultPreview } from "@/components/ToolLayout";
import { useState, useCallback, useRef, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { ToolInfoSection } from "@/components/ToolInfoSection";
import { NoticeBox } from "@/components/NoticeBox";
import { ReorderableFileList } from "@/components/ReorderableFileList";
import { useFreemium } from "@/hooks/useFreemium";
import { loadFFmpeg, fetchFile, checkFileSize, ffmpegOutputToBlob, resetFFmpegEngine } from "@/lib/ffmpegEngine";
import { mergeAudioFiles, AUDIO_FORMAT_OPTIONS, sameFormat, probeDuration, matchBitrate, matchCodec, formatDuration, crossfadeDurations, expectedMergeSeconds, mergeSampleRate, resampleNote, mergedFileName } from "./operations";
import { fileSampleRate } from "./containers";
import { AudioProcessingProgress, showAudioError, FileSizeNotice } from "./uiHelpers";
import type { FFmpeg } from "@ffmpeg/ffmpeg";

const TOOL_INFO_CONTENT = {
  what: `Combines two or more audio clips into a single file, played back to back in the order you set. You can leave a gap of silence between clips or crossfade them, up to 5 seconds. Runs entirely in your browser — your audio never leaves your device.`,
  how: `Upload your audio clips, then drag them (or use the arrows) into the order you want them to play. Each clip shows its length and sample rate. Choose a gap of silence or a crossfade between clips (they are alternatives), then merge and listen to the result before you download. Merging and listening cost nothing; a trial use is taken only when you download.`,
  tip: `Clips are re-encoded during merging (the output format defaults to the first clip's, at its bitrate), so mixing different source formats (like an MP3 and a WAV) won't break the merge. Clips recorded at different sample rates are all brought to the first clip's rate, and a note says which ones were. A crossfade is shortened where a clip is too short for it. The size limit applies to all clips added together, not each one separately.`,
};

const JOIN_CHOICES = [0, 0.25, 0.5, 1, 2, 3, 5];
const joinLabel = (n: number) => (n === 0 ? "none" : `${n} s`);

export default function AudioMerger() {
  const [files, setFiles] = useState<File[]>([]);
  const [processing, setProcessing] = useState(false);
  const [progress, setProgress] = useState(0);
  const [statusMessage, setStatusMessage] = useState("");
  const [result, setResult] = useState<{ blob: Blob; note: string; seconds: number | null } | null>(null);
  const [gap, setGap] = useState(0);
  const [crossfade, setCrossfade] = useState(0);
  const [format, setFormat] = useState("same");
  const [info, setInfo] = useState<Record<string, { duration: number | null; rate: number | null }>>({});
  const keyOf = (f: File) => `${f.name}|${f.size}|${f.lastModified}`;
  useEffect(() => {
    files.forEach((f) => {
      const k = keyOf(f);
      if (k in info) return;
      setInfo((d) => ({ ...d, [k]: { duration: null, rate: null } }));
      Promise.all([probeDuration(f), fileSampleRate(f)]).then(([duration, rate]) => setInfo((d) => ({ ...d, [k]: { duration, rate } })));
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [files]);
  const durations = files.map((f) => info[keyOf(f)]?.duration ?? null);
  const rates = files.map((f) => info[keyOf(f)]?.rate ?? null);
  const targetRate = mergeSampleRate(rates);
  const resampled = resampleNote(rates, targetRate);
  const outFormat = format === "same" ? sameFormat(files[0]?.name ?? "x.mp3") : AUDIO_FORMAT_OPTIONS.find((o) => o.value === format)!;
  const totalSec = expectedMergeSeconds(durations, gap, crossfade);
  const shortened = crossfade > 0 && crossfadeDurations(durations, crossfade).some((d) => d < crossfade - 1e-9);
  const { isPro, isLoading, isAdmin } = useFreemium();
  const ffmpegRef = useRef<FFmpeg | null>(null);
  const abortRef = useRef<AbortController | null>(null);

  const handleFiles = useCallback((newFiles: File[]) => {
    setFiles(prev => {
      const combined = [...prev];
      let duplicatesSkipped = 0;
      for (const f of newFiles) {
        const isDuplicate = combined.some(
          existing => existing.name === f.name && existing.size === f.size && existing.lastModified === f.lastModified
        );
        if (isDuplicate) duplicatesSkipped++;
        else combined.push(f);
      }
      if (duplicatesSkipped > 0) {
        toast.error(
          duplicatesSkipped === 1
            ? "That file is already in the list — skipped the duplicate."
            : `${duplicatesSkipped} of those files are already in the list — skipped the duplicates.`
        );
      }
      return combined;
    });
    setResult(null);
  }, []);

  const removeFile = (i: number) => { setFiles(prev => prev.filter((_, idx) => idx !== i)); setResult(null); };

  const sizeCheck = files.length > 0 ? checkFileSize(files, isPro || isLoading, isAdmin) : null;

  const stop = () => {
    abortRef.current?.abort();
    if (ffmpegRef.current) resetFFmpegEngine(ffmpegRef.current);
  };

  const merge = async () => {
    if (files.length < 2) { toast.error("Add at least 2 audio files to merge"); return; }
    // No trial use is taken here (B-0051, B-0055): merging and listening are free, and a merge that fails costs nothing.
    // The use is taken when the merged file is downloaded (DownloadButton below), once per visit, like Merge PDF.
    setProcessing(true);
    setProgress(0);
    const controller = new AbortController();
    abortRef.current = controller;
    try {
      setStatusMessage("Loading audio engine...");
      const ffmpeg = await loadFFmpeg((msg) => setStatusMessage(msg));
      ffmpegRef.current = ffmpeg;

      setStatusMessage("Merging clips...");
      // Lossy output: match the first clip's bitrate (never above it); lossless sources use the high level.
      const first = files[0];
      const codec = ["mp3", "ogg", "m4a"].includes(outFormat.value) ? matchCodec(outFormat.value, matchBitrate(first, durations[0])) : outFormat.codec;
      const data = await mergeAudioFiles(ffmpeg, files, fetchFile, setProgress, controller.signal, gap, { ext: outFormat.value, codec, durationSec: totalSec ?? undefined }, { crossfadeSeconds: crossfade, sampleRate: targetRate, durations });

      const blob = ffmpegOutputToBlob(data, outFormat.mime);
      setResult({ blob, note: resampled, seconds: totalSec });
      toast.success("Audio merged!");
    } catch (err) {
      showAudioError(err);
    } finally {
      if (ffmpegRef.current) resetFFmpegEngine(ffmpegRef.current);
      setProcessing(false);
    }
  };

  return (
    <ToolContainer>
      <FileDropZone accept="audio/*" multiple maxSize={500} onFiles={handleFiles} label="Drop audio files to merge" description="Combine multiple audio clips" deferUsageLog />
      {files.length > 0 && (
        <div className="mt-4 space-y-3">
          <ReorderableFileList
            files={files}
            onReorder={(next) => { setFiles(next); setResult(null); }}
            onRemove={removeFile}
            renderItemExtra={(i) => {
              const { duration, rate } = info[keyOf(files[i])] ?? { duration: null, rate: null };
              return (
                <span className="text-xs text-muted-foreground" data-testid="clip-info">
                  {i + 1}.{duration ? ` ${formatDuration(duration)}` : ""}{rate ? ` · ${+(rate / 1000).toFixed(2)} kHz` : ""}
                </span>
              );
            }}
          />
          <div className="flex flex-wrap items-center gap-x-6 gap-y-2 pt-1 text-sm">
            <label className="flex items-center gap-2">Gap between clips
              <select className="border rounded px-2 py-1 bg-background" aria-label="Gap between clips" value={gap} disabled={processing} onChange={(e) => { const v = Number(e.target.value); setGap(v); if (v > 0) setCrossfade(0); setResult(null); }}>
                {JOIN_CHOICES.map((n) => <option key={n} value={n}>{joinLabel(n)}</option>)}
              </select>
            </label>
            <label className="flex items-center gap-2">Crossfade
              <select className="border rounded px-2 py-1 bg-background" aria-label="Crossfade between clips" value={crossfade} disabled={processing} onChange={(e) => { const v = Number(e.target.value); setCrossfade(v); if (v > 0) setGap(0); setResult(null); }}>
                {JOIN_CHOICES.map((n) => <option key={n} value={n}>{joinLabel(n)}</option>)}
              </select>
            </label>
            <span className="text-xs text-muted-foreground">A gap and a crossfade are alternatives: choosing one clears the other.</span>
          </div>
          {shortened && <p className="text-xs text-muted-foreground" data-testid="crossfade-shortened">The crossfade is shortened where a clip is too short for it (at most half of either clip).</p>}
          <div className="flex flex-wrap items-center gap-2 pt-1">
            <span className="text-sm">Output format:</span>
            <Select value={format} onValueChange={(v) => { setFormat(v); setResult(null); }}>
              <SelectTrigger className="w-44" aria-label="Output format"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="same">Same as first file</SelectItem>
                {AUDIO_FORMAT_OPTIONS.map((o) => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}
              </SelectContent>
            </Select>
            {totalSec !== null && <span className="text-xs text-muted-foreground" data-testid="merge-total">Total length {formatDuration(totalSec)}</span>}
          </div>
          {resampled && <NoticeBox testId="merge-resample-note" className="text-sm">{resampled}. Mixed sample rates are brought to the first clip's rate.</NoticeBox>}
          {sizeCheck && <FileSizeNotice check={sizeCheck} isPro={isPro} />}
          {processing && <div className="pt-2"><AudioProcessingProgress progress={progress} statusMessage={statusMessage} onStop={stop} /></div>}
          {result && (
            <div className="space-y-1">
              <p className="text-sm font-medium">Listen to the result</p>
              <p className="text-xs text-muted-foreground" data-testid="merge-charge-note">Merging and listening cost nothing; a trial use is only taken when you download.</p>
              {result.note && <p className="text-xs text-muted-foreground" data-testid="merge-result-note">{result.note}.</p>}
              <ResultPreview blob={result.blob} />
            </div>
          )}
          <div className="flex gap-3 pt-4">
            {result ? (
              <>
                <DownloadButton blob={result.blob} fileName={mergedFileName(files.length, outFormat.value)} />
                <Button variant="outline" onClick={() => { setFiles([]); setResult(null); }}>New</Button>
              </>
            ) : (
              <>
                <Button onClick={merge} disabled={processing || files.length < 2 || sizeCheck?.severity !== "ok"}>{processing ? "Merging..." : "Merge Audio"}</Button>
                <Button variant="outline" onClick={() => setFiles([])} disabled={processing}>Clear</Button>
              </>
            )}
          </div>
        </div>
      )}
          <ToolInfoSection whatIsIt={TOOL_INFO_CONTENT.what} howToUse={TOOL_INFO_CONTENT.how} tip={TOOL_INFO_CONTENT.tip} />
    </ToolContainer>
  );
}
